import i18n from "@/i18n";
import { useCallback, useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { localizeDateNames } from "@/lib/dateNames";

// Copy last week (per employee): preview → confirm → saved draft results.
// The server re-plans and re-validates on save; nothing is published.

const t = (k, d, o) => i18n.t(`wf.copyLastWeek.${k}`, d, o);

export function fmtDay(ymd) {
    const s = new Date(`${ymd}T00:00:00Z`).toLocaleDateString("en-GB",
        { weekday: "short", day: "numeric", month: "numeric", timeZone: "UTC" });
    return localizeDateNames(s.replace(",", ""));
}

const fmtShift = (s, date) => `${s.start_time}–${s.end_time}${s.end_date && s.end_date !== date ? " (+1)" : ""}`;

const REASONS = {
    get ABSENCE() { return t("reason.absence", "absence on this day"); },
    get DAY_CODE() { return t("reason.dayCode", "has a day code"); },
    get MULTIPLE_SHIFTS() { return t("reason.multiple", "several shifts — edit this day manually"); },
    get NOT_APPLICABLE() { return t("reason.notApplicable", "not employed or not assigned here on this day"); },
    get OVERLAP() { return t("reason.overlap", "overlaps another shift"); },
    get PERIOD_CLOSED() { return t("reason.closed", "period closed"); },
    get EXISTING_DIFFERENT_SHIFT() { return t("reason.existing", "existing different shift kept"); },
    get WORKFORCE_SHIFT_OVERLAP() { return t("reason.overlap", "overlaps another shift"); },
    get WORKFORCE_REVISION_CONFLICT() { return t("reason.changed", "changed elsewhere — original kept"); },
    get WORKFORCE_PERIOD_CLOSED() { return t("reason.closed", "period closed"); },
    get WORKFORCE_SCHEDULE_NOT_APPLICABLE() { return t("reason.notApplicable", "not employed or not assigned here on this day"); },
    get WORKFORCE_PLAN_CELL_CONFLICT() { return t("reason.dayCode", "has a day code"); },
};
const reasonText = (code) => REASONS[code] || t("reason.notSaved", "not saved");

const STATUS = {
    get create() { return t("status.create", "Add"); },
    get replace() { return t("status.replace", "Different shift exists"); },
    get unchanged() { return t("status.unchanged", "Already the same"); },
    get skip() { return t("status.skip", "Skipped"); },
    get created() { return t("status.created", "Created"); },
    get replaced() { return t("status.replaced", "Replaced"); },
    get skipped() { return t("status.skipped", "Skipped"); },
    get failed() { return t("status.failed", "Failed"); },
};

function WeekPicker({ weeks, picked, onToggle }) {
    return (
        <div className="flex flex-wrap gap-3" data-testid="copy-last-week-week-picker">
            {weeks.map((w) => (
                <label key={w.week} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={picked.includes(w.week)} onCheckedChange={() => onToggle(w.week)}
                        data-testid={`copy-last-week-week-${w.week}`} />
                    {t("weekN", "Week {{n}}", { n: w.week })}
                    <span className="text-neutral-500">{fmtDay(w.start_date)} – {fmtDay(w.end_date)}</span>
                </label>
            ))}
            <Button variant="outline" size="sm" data-testid="copy-last-week-week-all"
                onClick={() => onToggle("all")}>{t("allWeeks", "All three weeks")}</Button>
        </div>
    );
}

function PreviewRow({ it, week, replaceOn, onReplace }) {
    const id = `${week}-${it.target_date}`;
    const cur = it.existing.map((s) => fmtShift(s, it.target_date)).join(", ");
    return (
        <tr className="border-b border-neutral-100" data-testid={`copy-last-week-item-${id}`}>
            <td className="py-1.5 pr-3 whitespace-nowrap">{fmtDay(it.target_date)}</td>
            <td className="py-1.5 pr-3 whitespace-nowrap">{it.shifts.map((s) => fmtShift(s, it.target_date)).join(", ")}</td>
            <td className="py-1.5 pr-3" data-testid={`copy-last-week-item-status-${id}`}>
                {STATUS[it.status]}{it.reason ? ` · ${reasonText(it.reason)}` : ""}
                {cur && it.status !== "unchanged" ? <span className="text-neutral-500"> ({t("now", "now")} {cur})</span> : null}
            </td>
            <td className="py-1.5">
                {it.status === "replace" ? (
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <Checkbox checked={replaceOn} onCheckedChange={onReplace}
                            data-testid={`copy-last-week-replace-${id}`} />
                        {t("replace", "Replace")}
                    </label>
                ) : null}
            </td>
        </tr>
    );
}

function ResultList({ result }) {
    const c = result.counts;
    return (
        <div data-testid="copy-last-week-results" className="space-y-2 text-sm">
            <p data-testid="copy-last-week-results-counts" className="font-medium">
                {t("counts", "Created {{created}} · Replaced {{replaced}} · Unchanged {{unchanged}} · Skipped {{skipped}} · Failed {{failed}}", c)}
            </p>
            <ul className="max-h-56 overflow-y-auto text-neutral-700">
                {result.results.filter((r) => r.result !== "created" && r.result !== "unchanged").map((r) => (
                    <li key={`${r.week}-${r.target_date}-${r.result}`} data-testid={`copy-last-week-result-${r.week}-${r.target_date}`}>
                        {fmtDay(r.target_date)} — {STATUS[r.result]}{r.reason ? `: ${reasonText(r.reason)}` : ""}
                    </li>
                ))}
            </ul>
            <p className="text-xs text-neutral-500">{t("draftNote", "Saved as unpublished draft changes. Publish when ready.")}</p>
        </div>
    );
}

export default function CopyLastWeekDialog({ open, employee, restaurantId, periodId, onClose, onApplied }) {
    const [plan, setPlan] = useState(null);
    const [picked, setPicked] = useState([1]);
    const [replace, setReplace] = useState({});
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(false);

    const load = useCallback(async () => {
        setPlan(null); setError(false);
        try {
            const { data } = await api.get("/workforce/schedule/copy-last-week", { params: {
                restaurant_id: restaurantId, period_id: periodId, employee_id: employee.employee_id } });
            setPlan(data);
        } catch (_e) { setError(true); }
    }, [restaurantId, periodId, employee]);
    useEffect(() => {
        if (!open || !employee) return;
        setPicked([1]); setReplace({}); setResult(null); load();
    }, [open, employee, load]);

    const toggle = (w) => setPicked((p) => (w === "all" ? [1, 2, 3]
        : p.includes(w) ? p.filter((x) => x !== w) : [...p, w].sort()));
    const weeks = (plan?.weeks || []).filter((w) => picked.includes(w.week));
    const actionable = weeks.some((w) => w.items.some((i) => i.status === "create"
        || (i.status === "replace" && replace[`${w.week}-${i.target_date}`])));
    const save = async () => {
        setBusy(true);
        const rep = [];
        weeks.forEach((w) => w.items.forEach((i) => {
            if (i.status === "replace" && replace[`${w.week}-${i.target_date}`]) {
                rep.push({ target_date: i.target_date, schedule_entry_id: i.existing[0].schedule_entry_id,
                    expected_revision: i.existing[0].revision });
            }
        }));
        try {
            const { data } = await api.post("/workforce/schedule/copy-last-week", { restaurant_id: restaurantId,
                period_id: periodId, employee_id: employee.employee_id, weeks: picked, replace: rep });
            setResult(data);
            await onApplied?.();
        } catch (_e) { setError(true); } finally { setBusy(false); }
    };
    const name = employee ? `${employee.first_name} ${employee.last_name}` : "";
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="max-w-2xl" data-testid="copy-last-week-dialog">
                <DialogHeader>
                    <DialogTitle data-testid="copy-last-week-title">{t("title", "Copy last week — {{name}}", { name })}</DialogTitle>
                    {plan ? (
                        <DialogDescription data-testid="copy-last-week-source">
                            {t("source", "Source: final week of the previous period, {{from}} – {{to}}", {
                                from: fmtDay(plan.source_start_date), to: fmtDay(plan.source_end_date) })}
                        </DialogDescription>
                    ) : null}
                </DialogHeader>
                {error ? <p className="text-sm text-red-700" data-testid="copy-last-week-error">{t("error", "Could not load or save. Nothing was changed by this step.")}</p> : null}
                {!plan && !error ? <p className="text-sm text-neutral-500">{t("loading", "Loading…")}</p> : null}
                {plan && !result && plan.source_shifts.length === 0 ? (
                    <p className="text-sm" data-testid="copy-last-week-no-source">
                        {t("noSource", "{{name}} has no planned shifts in {{from}} – {{to}}. Nothing to copy.", {
                            name, from: fmtDay(plan.source_start_date), to: fmtDay(plan.source_end_date) })}
                    </p>
                ) : null}
                {plan && !result && plan.source_shifts.length > 0 ? (
                    <div className="space-y-3">
                        <p className="text-sm text-neutral-700" data-testid="copy-last-week-source-shifts">
                            {plan.source_shifts.map((s) => `${fmtDay(s.work_date)} ${fmtShift(s, s.work_date)}`).join(" · ")}
                        </p>
                        <WeekPicker weeks={plan.weeks} picked={picked} onToggle={toggle} />
                        <div className="max-h-72 overflow-y-auto">
                            {weeks.map((w) => (
                                <table key={w.week} className="w-full text-sm mb-2" data-testid={`copy-last-week-preview-week-${w.week}`}>
                                    <caption className="text-left text-xs font-medium text-neutral-500 py-1">{t("weekN", "Week {{n}}", { n: w.week })}</caption>
                                    <tbody>{w.items.map((i) => (
                                        <PreviewRow key={i.target_date} it={i} week={w.week}
                                            replaceOn={Boolean(replace[`${w.week}-${i.target_date}`])}
                                            onReplace={(v) => setReplace((r) => ({ ...r, [`${w.week}-${i.target_date}`]: Boolean(v) }))} />
                                    ))}</tbody>
                                </table>
                            ))}
                        </div>
                        <p className="text-xs text-neutral-500">{t("rules", "Existing different shifts are kept unless you tick Replace. Absences and day codes are never changed. Changes stay unpublished.")}</p>
                    </div>
                ) : null}
                {result ? <ResultList result={result} /> : null}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="copy-last-week-close">
                        {result ? t("done", "Done") : i18n.t("common.cancel", "Cancel")}
                    </Button>
                    {!result && plan && plan.source_shifts.length > 0 ? (
                        <Button onClick={save} disabled={busy || !actionable} data-testid="copy-last-week-confirm">
                            {busy ? t("saving", "Saving…") : t("confirm", "Copy as draft")}
                        </Button>
                    ) : null}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
